/**
 * Enhanced security middleware and utilities for the application
 */

import { toast } from "@/components/ui/use-toast";
import { z } from "zod";

// Enhanced rate limiting with IP tracking
interface RateLimitState {
  count: number;
  timestamp: number;
  ips: Set<string>;
  userAgent: string;
}

const rateLimitStore: Map<string, RateLimitState> = new Map();
const MAX_REQUESTS = 50;
const TIME_WINDOW = 60000; // 1 minute
const MAX_IPS_PER_USER = 3;

/**
 * Get client IP address from request headers
 */
const getClientIP = (): string => {
  // In a real production environment, this would use request headers
  // For now we'll use a placeholder since we're client-side
  return "client-side";
};

/**
 * Enhanced rate limiting middleware for API calls
 * Now includes IP tracking and user agent logging
 */
export const shouldRateLimit = (endpoint: string): boolean => {
  const key = `${endpoint}:${getCurrentUserFingerprint()}`;
  const now = Date.now();
  const clientIP = getClientIP();
  const userAgent = navigator.userAgent;
  
  const state = rateLimitStore.get(key) || { 
    count: 0, 
    timestamp: now,
    ips: new Set([clientIP]),
    userAgent 
  };
  
  // Reset counter for new time window
  if (now - state.timestamp > TIME_WINDOW) {
    rateLimitStore.set(key, { 
      count: 1, 
      timestamp: now,
      ips: new Set([clientIP]),
      userAgent 
    });
    return false;
  }
  
  // Check for suspicious activity (multiple IPs)
  if (!state.ips.has(clientIP)) {
    if (state.ips.size >= MAX_IPS_PER_USER) {
      console.warn(`Suspicious activity detected for ${endpoint}: Multiple IPs`);
      return true;
    }
    state.ips.add(clientIP);
  }
  
  // Increment counter for existing time window
  if (state.count >= MAX_REQUESTS) {
    console.warn(`Rate limit exceeded for ${endpoint}`);
    return true;
  }
  
  rateLimitStore.set(key, { 
    count: state.count + 1, 
    timestamp: state.timestamp,
    ips: state.ips,
    userAgent 
  });
  
  return false;
};

/**
 * Generate a simple fingerprint for the current user
 * This is a basic implementation - in production, use a more robust solution
 */
export const getCurrentUserFingerprint = (): string => {
  const { userAgent, language } = navigator;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const timezoneOffset = new Date().getTimezoneOffset();
  
  return `${userAgent}:${language}:${screenWidth}x${screenHeight}:${timezoneOffset}`;
};

/**
 * Enhanced input sanitization with additional security measures
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  return input
    .replace(/[<>]/g, '') // Remove < and > characters
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/data:/gi, '') // Remove data: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/\\/g, '\\\\') // Escape backslashes
    .replace(/'/g, '\\\'') // Escape single quotes
    .trim();
};

/**
 * Validates if a string is a valid UUID
 */
export const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(uuid);
};

/**
 * Input validation schemas using Zod
 */
export const validationSchemas = {
  uuid: z.string().uuid(),
  email: z.string().email(),
  password: z.string().min(8).regex(/[A-Z]/).regex(/[a-z]/).regex(/[0-9]/),
  username: z.string().min(3).max(50).regex(/^[a-zA-Z0-9_-]+$/),
  url: z.string().url(),
  positiveNumber: z.number().positive(),
  
  // Common validation helper
  validate: <T>(schema: z.ZodType<T>, data: unknown): { success: boolean; data?: T; error?: string } => {
    try {
      const validated = schema.parse(data);
      return { success: true, data: validated };
    } catch (error) {
      if (error instanceof z.ZodError) {
        return { success: false, error: error.errors[0].message };
      }
      return { success: false, error: 'Validation failed' };
    }
  }
};

/**
 * Security logging utility
 */
export const securityLog = (
  event: string,
  details: Record<string, any>,
  level: 'info' | 'warn' | 'error' = 'info'
): void => {
  const log = {
    timestamp: new Date().toISOString(),
    event,
    level,
    fingerprint: getCurrentUserFingerprint(),
    userAgent: navigator.userAgent,
    ...details
  };
  
  console[level]('[Security]', log);
  
  // For critical security events, notify the user
  if (level === 'error') {
    toast({
      title: "Security Alert",
      description: "A security-related error has occurred. Please try again or contact support.",
      variant: "destructive",
    });
  }
};

// Additional security utilities
export const securityUtils = {
  /**
   * Validate that a value is a safe integer
   */
  isSafeInteger: (value: unknown): boolean => {
    const num = Number(value);
    return Number.isSafeInteger(num);
  },
  
  /**
   * Check if a string contains potential SQL injection patterns
   */
  hasSQLInjection: (value: string): boolean => {
    const sqlPatterns = /('|--|;|\/\*|\*\/|xp_|sp_|exec|SELECT|INSERT|UPDATE|DELETE|DROP|UNION|INTO|LOAD_FILE)/gi;
    return sqlPatterns.test(value);
  },
  
  /**
   * Create a secure hash of sensitive data (for comparison purposes)
   */
  createHash: async (data: string): Promise<string> => {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
};

/**
 * Enhanced fetch wrapper with security measures
 * @param url API URL
 * @param options Fetch options
 */
export const secureFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  if (shouldRateLimit(url)) {
    throw new Error('Rate limit exceeded. Please try again later.');
  }
  
  const headers = new Headers(options.headers || {});
  headers.append('X-Client-Fingerprint', getCurrentUserFingerprint());
  
  return fetch(url, {
    ...options,
    headers
  });
};
